// Skeletons (DESIGN_SYSTEM §6.2, §9.1): layout-true placeholders with a slow opacity pulse, shown
// only after 300 ms. Decorative: the busy region carries aria-busy and a hidden label instead.
import type { ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx, useDelayedFlag } from "../lib";

export function Skeleton({ className }: { className?: string }) {
  return <span className={cx("ady-skeleton block h-4", className)} aria-hidden="true" />;
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span className={cx("flex flex-col gap-2", className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={i === lines - 1 ? "w-2/3" : "w-full"} />
      ))}
    </span>
  );
}

export interface BusyRegionProps {
  busy: boolean;
  /** The skeleton layout, rendered after the delay. */
  skeleton: ReactNode;
  children: ReactNode;
  label?: string;
  className?: string;
  "data-testid"?: string;
}

/** A region that is aria-busy while loading and swaps in its skeleton after 300 ms. */
export function BusyRegion({
  busy,
  skeleton,
  children,
  label,
  className,
  "data-testid": testId,
}: BusyRegionProps) {
  const showSkeleton = useDelayedFlag(busy);
  return (
    <div
      aria-busy={busy || undefined}
      className={className}
      data-testid={testId}
      data-state={busy ? "loading" : "ready"}
    >
      {busy ? (
        <>
          <span className="ady-sr-only">{label ?? t("states.loadingRegion")}</span>
          {showSkeleton ? skeleton : null}
        </>
      ) : (
        children
      )}
    </div>
  );
}
